import { memo, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, useBreakpoint } from "@chakra-ui/react";
import useResizeObserver from "@react-hook/resize-observer";
import { ContentSectionCustomCard } from "../../types";
import { useAccountData } from "../../hooks/useAccountData";
import { ButtonCarousel } from "../carousel/ButtonCarousel";
import {
  ChannelButtonCard,
  getBreakpointConfig,
  getHeightFromWidth,
} from "./ChannelButtonCard";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";

// Center image type ID from the database
const CENTER_IMAGE_TYPE_ID = 19;

interface CenterImageContainerProps {
  data: ContentSectionCustomCard[];
  size: "s" | "m";
}

export const CenterImageContainer = memo(
  ({ data }: CenterImageContainerProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const breakpoint = useBreakpoint({ ssr: false });
    const isSmall = useIsSmallBreakpoint();

    // Get initial container width on mount
    useLayoutEffect(() => {
      if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        if (width > 0) setContainerWidth(width);
      }
    }, []);

    // Update container width on resize
    useResizeObserver(containerRef, (entry) => {
      const width = entry.contentRect.width;
      if (width > 0) setContainerWidth(width);
    });

    const accountData = useAccountData();
    const channels = accountData?.home_tab_channels;

    // Use Map for O(1) lookups instead of O(n) find operations
    const streamMap = useMemo(() => {
      const map = new Map<string, string>();

      // Add originals channel
      const originalsChannel = accountData?.originals_channel?.streams[0];
      if (originalsChannel) {
        map.set(originalsChannel.id, "originals");
      }

      // Add all other channels
      channels?.forEach((channel) => {
        channel.streams.forEach((stream) => {
          map.set(stream.id, stream.slug);
        });
      });

      return map;
    }, [channels, accountData?.originals_channel]);

    // Extract cards with center images or center elements (text/cta)
    const cardsWithCenterImages = useMemo(() => {
      return data
        .map((card) => {
          const centerImage = card.images.find(
            (img) => img.image_type_id === CENTER_IMAGE_TYPE_ID,
          );
          if (!centerImage && !card.center_element) return null;

          const urlLink = card.cta_url ?? "";
          const ctaUrl = streamMap.get(urlLink);
          const link = urlLink.startsWith("_") ? `/channel/${ctaUrl}` : urlLink;

          return {
            card,
            imageUrl: centerImage?.medium_url ?? null,
            link,
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);
    }, [data, streamMap]);

    // Get breakpoint-specific configuration
    const { cardWidth: targetCardWidth, gap } = getBreakpointConfig(breakpoint);

    // Use square cards only for containerWidth <= 500, rectangular for larger
    const isSquare = containerWidth <= 500;
    const aspectRatio = isSquare ? 1 : 1.63;
    // Scale border radius: 12 for square, 16 for small rectangle, 24 for large
    const borderRadius = isSquare ? 12 : containerWidth <= 800 ? 16 : 24;
    const imageSize = isSquare ? 75 : 60;

    const cardCount = cardsWithCenterImages.length;
    const totalGaps = cardCount > 0 ? (cardCount - 1) * gap : 0;
    const horizontalPadding = gap; // Padding on each side when cards fit

    // Calculate how many cards fit at target width
    const cardsFittingAtTargetWidth =
      containerWidth > 0
        ? Math.floor((containerWidth + gap) / (targetCardWidth + gap))
        : cardCount;

    // Determine if we need to scroll
    const needsScroll = cardCount > cardsFittingAtTargetWidth;

    // Calculate final card width
    let cardWidth: number;
    if (needsScroll) {
      // Too many cards - use target width and scroll
      cardWidth = targetCardWidth;
    } else if (cardCount > 0 && containerWidth > 0) {
      // Cards fit - stretch to fill container with padding on right
      const availableWidth = containerWidth - horizontalPadding;
      cardWidth = (availableWidth - totalGaps) / cardCount;
    } else {
      cardWidth = targetCardWidth;
    }

    // Height is fixed based on target width, only width stretches
    const cardHeight = getHeightFromWidth(targetCardWidth, aspectRatio);

    // Calculate how many items to scroll at once
    const slidesToScroll = Math.max(
      1,
      Math.floor(containerWidth / (cardWidth + gap)) - 1,
    );

    if (cardsWithCenterImages.length === 0) {
      return null;
    }

    // Stable wrapper keeps ref attached to same element across renders
    return (
      <Box ref={containerRef} width="100%">
        {!needsScroll ? (
          // When cards fit - stretched flex row
          <Flex
            justify="center"
            gap={`${gap}px`}
            width="100%"
            pr={`${horizontalPadding}px`}
          >
            {cardsWithCenterImages.map(({ card, imageUrl, link }) => (
              <ChannelButtonCard
                key={card.id}
                card={card}
                imageUrl={imageUrl}
                link={link}
                isSmall={isSmall}
                cardWidth={cardWidth}
                cardHeight={cardHeight}
                borderRadius={borderRadius}
                imageSize={imageSize}
              />
            ))}
          </Flex>
        ) : (
          // When cards don't fit - scrollable carousel
          <ButtonCarousel
            width="100%"
            slidesToScroll={slidesToScroll}
            containerProps={{ gap: `${gap}px` }}
            big
          >
            {cardsWithCenterImages.map(({ card, imageUrl, link }) => (
              <ChannelButtonCard
                key={card.id}
                card={card}
                imageUrl={imageUrl}
                link={link}
                isSmall={isSmall}
                cardWidth={cardWidth}
                cardHeight={cardHeight}
                borderRadius={borderRadius}
                imageSize={imageSize}
              />
            ))}
          </ButtonCarousel>
        )}
      </Box>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.data === nextProps.data && prevProps.size === nextProps.size
    );
  },
);
